import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../Contexts/APIContext";
import FormControlLabel from "@mui/material/FormControlLabel";
import SalesFiltersViewModel from "./ViewModels/SalesFiltersViewModel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { TextFieldValidator } from "../Utility/InputValidation";
import { handleAutoCompleteChange } from "../Utility/FilterChangeHandlers";
import Chip from "@mui/material/Chip";

interface ISalesFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function SalesFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: ISalesFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [salesFilters, setSalesFilters] = useState<SalesFiltersViewModel>(callTableViewModel.filters.salesFilters);
    const [saleTypes, setSaleTypes] = useState<string[]>([]);
    const [productTypes, setProductTypes] = useState<string[]>([]);
    const [products, setProducts] = useState<string[]>([]);
    const [productOffers, setProductOffers] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setSalesFilters(callTableViewModel.filters.salesFilters)
            //console.log(salesFilters.product.join(", "));
        }
    }, [open])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('product', `GetSaleTypeOptions?client=${callTableViewModel.filters.client}`, (data) => setSaleTypes(data));
            get('product', `GetProductTypeOptions?client=${callTableViewModel.filters.client}`, (data) => setProductTypes(data));
            get('product', `GetProductOptions?client=${callTableViewModel.filters.client}`, (data) => setProducts(data));
            get('product', `GetProductOfferOptions?client=${callTableViewModel.filters.client}`, (data) => setProductOffers(data));
        }
    }, [callTableViewModel.filters.client])

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const inputProps: InputProps[] = [
        {
            name: 'saleType',
            label: 'Sale Type',
            value: salesFilters.saleType,
            options: saleTypes,
            disabled: !salesFilters.sale,
            type: InputType.AutoComplete
        },
        {
            name: 'productType',
            label: 'Product Type',
            value: salesFilters.productType,
            options: productTypes,
            disabled: !salesFilters.sale,
            type: InputType.AutoComplete
        },
        {
            name: 'product',
            label: 'Product',
            value: salesFilters.product,
            options: products,
            disabled: !salesFilters.sale,
            type: InputType.AutoComplete
        },
        {
            name: 'productOffer',
            label: 'Product Offer',
            value: salesFilters.productOffer,
            options: productOffers,
            disabled: !salesFilters.sale,
            type: InputType.AutoComplete
        },
        {
            name: 'productReference',
            label: 'Product Reference',
            value: salesFilters.productReference,
            disabled: !salesFilters.sale,
            type: InputType.TextField
        },
    ];

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                {
                    inputProps.type === InputType.TextField && !inputProps.disabled &&
                    <>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <TextField
                            name={inputProps.name}
                            onChange={handleSaleFilterChange}
                            value={inputProps.value}
                            fullWidth
                            autoComplete="off"
                            aria-autocomplete="none"
                            disabled={inputProps.disabled} />
                    </>
                }
                {inputProps.type === InputType.AutoComplete && !inputProps.disabled &&
                    <>
                        <InputLabel>{inputProps.label}</InputLabel>
                        {inputProps.options!.length >= 100 ?
                            <>
                                <Tooltip title={<>Search seperates on commas</>}>
                                    <TextField
                                        name={inputProps.name}
                                        onChange={handleSaleFilterChangeFieldInput}
                                        value={inputProps.value.toString()}
                                        fullWidth
                                        disabled={inputProps.disabled}
                                        autoComplete="off"
                                        aria-autocomplete="none" />
                                </Tooltip>
                            </>
                            :
                            <>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    disabled={inputProps.disabled}
                                    options={inputProps.options!}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option}
                                    value={inputProps.value as string[]}
                                    onChange={(event: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setSalesFilters) }}
                                    limitTags={3}
                                    renderOption={(props, option, { selected }) => {
                                        const { ...optionProps } = props;
                                        return (
                                            <li {...optionProps}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth />
                                    )}
                                    renderTags={(value, getTagProps) => {
                                        const numTags = value.length;
                                        const limitTags = 1;
                                        return (
                                            <>
                                                {value.slice(0, limitTags).map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({ index })}
                                                        key={index}
                                                        label={option}
                                                    />
                                                ))}

                                                {numTags > limitTags && ` +${numTags - limitTags}`}
                                            </>
                                        );
                                    }}
                                />
                            </>
                        }
                    </>
                }
            </Grid>
        )
    }

    const handleSaleFilterChange = (e: any) => {
        if (TextFieldValidator(e.target.value))
            setSalesFilters({
                ...salesFilters,
                [e.target.name]: e.target.value
            })
    }

    //convert a passed in string to a list of string sperated by a comma, as the db search wants a list
    const handleSaleFilterChangeFieldInput = (e: any) => {
        let valueList: string[] = e.target.value.split(',');

        setSalesFilters({
            ...salesFilters,
            [e.target.name]: valueList
        })
    }

    const handleSaleChange = (value: boolean) => {
        const newSaleFilters = { ...salesFilters };
        newSaleFilters.sale = value;

        if (!value) {
            newSaleFilters.saleType = [];
            newSaleFilters.productType = [];
            newSaleFilters.product = [];
            newSaleFilters.productOffer = [];
            newSaleFilters.productReference = '';
        }

        setSalesFilters({ ...newSaleFilters });
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        //this removes the empty values and trailing spaces from any maunially inputed list << stops UI confusion and DB filtering on empty/space
        newCallTableViewModel.filters.salesFilters = {
            ...salesFilters,
            saleType: salesFilters.saleType.map(item => item.trim()).filter(f => f !== ''),
            product: salesFilters.product.map(item => item.trim()).filter(f => f !== ''),
            productType: salesFilters.productType.map(item => item.trim()).filter(f => f !== ''),
            productOffer: salesFilters.productOffer.map(item => item.trim()).filter(f => f !== ''),
        };

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Sales Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    <Grid container size={12} justifyContent="space-between">
                        <Grid>
                            <FormControlLabel control={<Checkbox checked={salesFilters.sale} onChange={() => handleSaleChange(true)} />} label="Sale" />
                        </Grid>
                        <Grid>
                            <FormControlLabel control={<Checkbox checked={!salesFilters.sale} onChange={() => handleSaleChange(false)} />} label="No Sale" />
                        </Grid>
                    </Grid>
                    {
                        inputProps.map(props => getInput(props))
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}